import React, { Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Star, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { clients } from '../lib/data';
import { Button } from '../components/ui/button';
const clientSentiment: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
}> = {
  etoro: {
    positive: 52,
    neutral: 38,
    negative: 22
  },
  mastercard: {
    positive: 41,
    neutral: 35,
    negative: 13
  },
  ibm: {
    positive: 68,
    neutral: 55,
    negative: 33
  }
};
export function ClientSelection() {
  const navigate = useNavigate();
  return <div className="min-h-screen flex flex-col" style={{
    backgroundColor: '#010827'
  }} data-id="element-761">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-4" data-id="element-762">
        <div className="flex items-center px-1" data-id="element-763">
          <img src="https://cdn.magicpatterns.com/uploads/s2MFPhMtmvBfy6H1KoBtds/Unione_Comunita_Ebraiche_Italiane_-_logo_(Italy).svg" alt="UCEI — Unione delle Comunità Ebraiche Italiane" className="h-10 w-auto object-contain" style={{
          filter: 'brightness(0) invert(1)'
        }} data-id="element-764" />
        </div>
        <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-10 px-4 text-sm gap-2" data-id="element-765">
          <LogOut className="h-4 w-4" data-id="element-766" />
          Esci
        </Button>
      </div>

      {/* Centered Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 -mt-16" data-id="element-767">
        <motion.h1 initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-2xl md:text-3xl font-normal tracking-tight text-white/80 mb-10" data-id="element-768">
          Seleziona un cliente
        </motion.h1>

        <motion.div initial="hidden" animate="visible" variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1
          }
        }
      }} className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full max-w-4xl" data-id="element-769">
          {clients.map(client => {
          const sentiment = clientSentiment[client.id] || {
            positive: 0,
            neutral: 0,
            negative: 0
          };
          return <motion.button key={client.id} variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} onClick={() => {
            localStorage.setItem('selectedClientId', client.id);
            window.dispatchEvent(new Event('client-changed'));
            navigate('/home');
          }} className="w-full flex flex-col rounded-2xl bg-white border border-border hover:border-[#0187c3]/50 hover:shadow-[0_0_30px_rgba(1,135,195,0.12)] transition-all duration-300 group text-left p-6" data-id="element-770">
                {/* Header Area */}
                <div className="flex items-center gap-4 mb-8" data-id="element-771">
                  <div className="w-12 h-12 rounded-full bg-gray-100 p-2 flex items-center justify-center flex-shrink-0" data-id="element-772">
                    <img src={client.logo} alt={client.name} className="max-w-full max-h-full object-contain" data-id="element-773" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-774">
                    <div className="text-lg text-foreground truncate font-normal group-hover:text-[#0187c3] transition-colors" data-id="element-775">
                      {client.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-normal" data-id="element-776">
                      Ultimo agg:{' '}
                      {new Date(client.lastUpdate).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                    </div>
                  </div>
                </div>

                {/* KPIs Area */}
                <div className="space-y-6" data-id="element-777">
                  <div className="grid grid-cols-2 gap-4" data-id="element-778">
                    <div data-id="element-779">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-normal" data-id="element-780">
                        Articoli / mese
                      </div>
                      <div className="text-xl text-foreground font-light" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-781">
                        {client.articlesThisMonth}
                      </div>
                    </div>
                    <div data-id="element-782">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-normal" data-id="element-783">
                        Score medio
                      </div>
                      <div className="text-xl text-[#0187c3] font-light" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-784">
                        {client.overallScore.toFixed(1)}
                      </div>
                    </div>
                  </div>

                  {/* Sentiment Area */}
                  <div data-id="element-785">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-normal" data-id="element-786">
                      Sentiment
                    </div>
                    <div className="flex items-center gap-4" data-id="element-787">
                      <div className="flex items-center gap-1.5" data-id="element-788">
                        <span className="w-3 h-3 rounded-full bg-[#0187c3]" data-id="element-789" />
                        <span className="text-muted-foreground text-sm" data-id="element-790">
                          {sentiment.positive}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-791">
                        <span className="w-3 h-3 rounded-full bg-[#64B5F6]" data-id="element-792" />
                        <span className="text-muted-foreground text-sm" data-id="element-793">
                          {sentiment.neutral}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-794">
                        <span className="w-3 h-3 rounded-full bg-[#1A3A5C]" data-id="element-795" />
                        <span className="text-muted-foreground text-sm" data-id="element-796">
                          {sentiment.negative}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.button>;
        })}
        </motion.div>
      </div>
    </div>;
}